import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { DeleteOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Input, message, Modal, Popconfirm, Space, Table, Tag, Typography } from "antd";
import moment from "moment";
import AiVideoProjectForm, { isIllustratedProject } from "../components/aiVideo/AiVideoProjectForm";
import { googleWorkspaceApi } from "../services/googleWorkspaceApi";
import { promptApi } from "../services/promptApi";
import { aiVideoApi } from "../services/aiVideoApi";
import { useSessionProfile } from "../services/SessionProfileContext";

const { Text, Title } = Typography;
const ACTIVE_STATUSES = new Set(["QUEUED", "LEASED", "RUNNING"]);
const EDITABLE_STATUSES = new Set(["DRAFT", "COMPLETED", "FAILED", "CANCELLED"]);

export function aiVideoQuotaLabel(quota) {
  if (!quota || !Number.isInteger(quota.dailyLimit) || !Number.isInteger(quota.usedToday) || !Number.isInteger(quota.remaining)) {
    return "Không tải được hạn mức hiện tại";
  }
  if (quota.dailyLimit === -1) return `Không giới hạn · đã dùng ${quota.usedToday} hôm nay`;
  if (quota.dailyLimit === 0) return "Đã chặn tạo video hôm nay";
  return `Còn ${Math.max(0, quota.remaining)}/${quota.dailyLimit} · đã dùng ${quota.usedToday} hôm nay`;
}

export function aiVideoProjectTypeLabel(projectType) {
  return projectType === "GOOGLE_SHEETS" ? "Render theo Google Sheets" : "Micro Story";
}

export function aiVideoProjectActions(project, profile, quota, providers = []) {
  const owner = Boolean(profile?.id && String(project?.ownerId) === String(profile.id));
  const enabledPlan = profile?.aiVideoPlanMode === "BYOK" || profile?.aiVideoPlanMode === "MANAGED";
  const status = project?.status;
  const active = ACTIVE_STATUSES.has(status);
  const automationActive = project?.automationActive === true;
  const byokProviderReady = profile?.aiVideoPlanMode !== "BYOK" || (Array.isArray(providers) ? providers : []).some((provider) =>
    String(provider?.id) === String(project?.aiProviderId)
    && provider?.enabled === true
    && provider?.credentialScope === "USER"
    && String(provider?.ownerId) === String(profile?.id)
    && Array.isArray(provider?.capabilities)
    && provider.capabilities.some((capability) => String(capability).trim().toLowerCase() === "script")
  );
  const quotaAllowsNewGeneration = quota?.remaining === -1 || (Number.isInteger(quota?.remaining) && quota.remaining > 0);
  const quotaAllowsRetry = Number.isInteger(quota?.dailyLimit) && quota.dailyLimit !== 0;
  const selectedChannels = project?.selectedChannels && typeof project.selectedChannels === "object" ? project.selectedChannels : {};
  const hasUploadDestination = Boolean(project?.uploadGroupId) || ["youtubeChannelIds", "tiktokAccountIds", "facebookPageIds"]
    .some((key) => Array.isArray(selectedChannels[key]) && selectedChannels[key].length > 0);
  const canRunWithoutUploadDestination = project?.projectType === "GOOGLE_SHEETS"
    || (project?.projectType === "MICRO_STORY" && project?.scriptSource === "GOOGLE_SHEETS");
  const runDestinationReady = hasUploadDestination || canRunWithoutUploadDestination;
  const completed = status === "COMPLETED";
  return {
    canEdit: owner && enabledPlan,
    canDelete: owner && !automationActive && !active,
    canStart: owner && enabledPlan && byokProviderReady && !automationActive && !active && quotaAllowsNewGeneration && runDestinationReady,
    canStop: owner && enabledPlan && automationActive,
    canGenerate: owner && enabledPlan && byokProviderReady && !active && EDITABLE_STATUSES.has(status) && quotaAllowsNewGeneration && runDestinationReady,
    canRetry: owner && enabledPlan && byokProviderReady && quotaAllowsRetry && !active && (status === "FAILED" || status === "CANCELLED") && Number(project?.generationNumber) > 0,
    canCancel: owner && enabledPlan && active,
    canDownload: owner && completed,
    canEnqueue: owner && completed && hasUploadDestination,
  };
}

export function safeAiVideoDownloadUrl(value) {
  try {
    const url = new URL(String(value || ""));
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : null;
  } catch (_) { return null; }
}

export function openAiVideoDownload(value) {
  const url = safeAiVideoDownloadUrl(value);
  if (!url) throw new Error("AI_VIDEO_DOWNLOAD_INVALID");
  const link = document.createElement("a");
  link.href = url;
  try {
    link.download = decodeURIComponent(url.pathname.split("/").pop() || "") || "ai-video.mp4";
  } catch (_) {
    link.download = "ai-video.mp4";
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function safeAiVideoErrorMessage(error, fallback = "Không thể hoàn tất thao tác AI Video. Vui lòng thử lại.") {
  const value = String(error?.message || "").toLowerCase();
  if (value.includes("không có dòng status=ready")) return "Chưa có dòng STATUS=READY trong Google Sheet.";
  if (value.includes("thiếu column storyboard")) return "Google Sheet thiếu cột storyboard. Hãy kiểm tra STORYBOARD_STATUS, STORYBOARD_JSON, STORYBOARD_ERROR và vùng dữ liệu bao gồm cột N.";
  if (value.includes("storyboard_json")) return "Storyboard trong Google Sheet không hợp lệ. Xem cột STORYBOARD_ERROR, sửa JSON rồi đặt STATUS=READY.";
  if (value.includes("giới hạn") || value.includes("limit") || value.includes("quota")) return "Đã đạt giới hạn tạo video AI hôm nay.";
  if (value.includes("provider") || value.includes("nhà cung cấp")) return "Nhà cung cấp AI không còn hợp lệ. Vui lòng kiểm tra lại cấu hình.";
  if (value.includes("job đang") || value.includes("active job")) return "Dự án đang có một job hoạt động. Vui lòng chờ job hiện tại kết thúc.";
  if (value.includes("không hợp lệ") || value.includes("validation")) return "Yêu cầu không hợp lệ. Vui lòng kiểm tra các trường đã nhập.";
  return fallback;
}

const formatDateTime = (value) => {
  const date = value ? moment(value) : null;
  return date?.isValid() ? date.format("DD/MM/YYYY HH:mm") : "-";
};

export const AI_VIDEO_BASE_PATH = "/video-tools/ai-video";
export const ILLUSTRATED_VIDEO_BASE_PATH = "/video-tools/illustrated-video";
export const aiVideoProjectPath = (project) =>
  `${isIllustratedProject(project) ? ILLUSTRATED_VIDEO_BASE_PATH : AI_VIDEO_BASE_PATH}/${project.id}`;

export function IllustratedVideoProjects() {
  return <AiVideoProjects variant="ILLUSTRATED" />;
}

export default function AiVideoProjects({ variant = "STANDARD" }) {
  const illustrated = variant === "ILLUSTRATED";
  const { profile, status } = useSessionProfile();
  const history = useHistory();
  const mounted = useRef(true);
  const loadGeneration = useRef(0);
  const [projects, setProjects] = useState([]);
  const [providers, setProviders] = useState([]);
  const [workspaceConnections, setWorkspaceConnections] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState({});

  useEffect(() => () => { mounted.current = false; loadGeneration.current += 1; }, []);

  const load = useCallback(async () => {
    if (status !== "ready") return;
    const requestGeneration = ++loadGeneration.current;
    setLoading(true);
    const settled = (promise) => promise.then((value) => ({ value })).catch((error) => ({ error }));
    try {
      const [projectResult, quotaResult, providerResult, workspaceResult] = await Promise.all([
        settled(aiVideoApi.projects.list()),
        settled(aiVideoApi.quota()),
        settled(profile.aiVideoPlanMode === "BYOK" ? promptApi.providers.list() : Promise.resolve([])),
        settled(googleWorkspaceApi.connections()),
      ]);
      if (!mounted.current || requestGeneration !== loadGeneration.current) return;
      if (projectResult.error) message.error(safeAiVideoErrorMessage(projectResult.error, "Không thể tải danh sách dự án AI Video."));
      else setProjects(Array.isArray(projectResult.value) ? projectResult.value : []);
      setQuota(quotaResult.error ? null : quotaResult.value || null);
      setProviders(providerResult.error || !Array.isArray(providerResult.value) ? [] : providerResult.value);
      setWorkspaceConnections(workspaceResult.error || !Array.isArray(workspaceResult.value) ? [] : workspaceResult.value);
    } finally {
      if (mounted.current && requestGeneration === loadGeneration.current) setLoading(false);
    }
  }, [profile.aiVideoPlanMode, status]);

  useEffect(() => { load(); }, [load]);

  const createProject = async (payload) => {
    if (creating || profile.aiVideoPlanMode === "DISABLED") return;
    setCreating(true);
    try {
      const { googleSheetsConfig, ...projectPayload } = payload;
      const created = await aiVideoApi.projects.create(projectPayload);
      if (payload.projectType === "GOOGLE_SHEETS" || payload.scriptSource === "GOOGLE_SHEETS") {
        try {
          await googleWorkspaceApi.triggers.create({ ...googleSheetsConfig, aiVideoProjectId: created.id });
        } catch (triggerError) {
          await aiVideoApi.projects.delete(created.id).catch(() => undefined);
          throw triggerError;
        }
      }
      if (!mounted.current) return;
      message.success("Đã tạo dự án AI Video.");
      setCreateOpen(false);
      await load();
      if (created?.id && mounted.current) history.push(aiVideoProjectPath({ ...payload, ...created }));
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể tạo dự án AI Video."));
    } finally {
      if (mounted.current) setCreating(false);
    }
  };

  const runAction = async (project, action) => {
    const actions = aiVideoProjectActions(project, profile, quota, providers);
    const permission = { start: "canStart", stop: "canStop" }[action];
    if (!permission || !actions[permission] || pending[project.id]) return;
    setPending((value) => ({ ...value, [project.id]: action }));
    try {
      await aiVideoApi.projects[action](project.id);
      if (!mounted.current) return;
      message.success(action === "stop" ? "Đã dừng dự án và gửi yêu cầu dừng job hiện tại." : "Đã bật dự án; hệ thống sẽ tự tạo video theo lịch.");
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error));
    } finally {
      if (mounted.current) {
        setPending((value) => { const next = { ...value }; delete next[project.id]; return next; });
        await load();
      }
    }
  };

  const connectWorkspace = async () => {
    try {
      const result = await googleWorkspaceApi.connect();
      const popup = window.open(result.authorizationUrl, "google-workspace-connect", "width=720,height=760");
      if (!popup) return message.warning("Trình duyệt đã chặn cửa sổ kết nối Google.");
      const timer = window.setInterval(async () => {
        if (!popup.closed) return;
        window.clearInterval(timer);
        try {
          const connections = await googleWorkspaceApi.connections();
          if (mounted.current) setWorkspaceConnections(Array.isArray(connections) ? connections : []);
        } catch (error) { if (mounted.current) message.error(error.message); }
      }, 800);
    } catch (error) { message.error(error.message); }
  };

  const deleteProject = async (project) => {
    if (pending[project.id]) return;
    setPending((value) => ({ ...value, [project.id]: "delete" }));
    try {
      await aiVideoApi.projects.delete(project.id);
      if (mounted.current) message.success("Đã xoá dự án AI Video và lịch sử liên quan.");
    } catch (error) {
      if (mounted.current) message.error(safeAiVideoErrorMessage(error, "Không thể xoá dự án AI Video."));
    } finally {
      if (mounted.current) {
        setPending((value) => { const next = { ...value }; delete next[project.id]; return next; });
        await load();
      }
    }
  };

  const filteredProjects = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    // Each menu only shows its own projects; the classic list never shows illustrated ones.
    const scoped = projects.filter((project) => isIllustratedProject(project) === illustrated);
    return keyword ? scoped.filter((project) => `${project.name || ""} ${project.topic || ""}`.toLowerCase().includes(keyword)) : scoped;
  }, [projects, search, illustrated]);

  const columns = [
    { title: "DỰ ÁN", render: (_, project) => <div><Link to={aiVideoProjectPath(project)}><Text strong>{project.name}</Text></Link><br /><Text type="secondary" ellipsis style={{ maxWidth: 280 }}>{project.topic}</Text></div> },
    { title: "LOẠI DỰ ÁN", width: 190, render: (_, project) => <Tag color={project.projectType === "GOOGLE_SHEETS" ? "blue" : "purple"}>{aiVideoProjectTypeLabel(project.projectType)}</Tag> },
    { title: "TỰ ĐỘNG", width: 140, render: (_, project) => <Tag color={project.automationActive ? "green" : "default"}>{project.automationActive ? "Đang chạy" : "Đã dừng"}</Tag> },
    { title: "LẦN CHẠY TIẾP", width: 165, render: (_, project) => formatDateTime(project.nextRunAt) },
    { title: "TẠO / CẬP NHẬT", width: 175, render: (_, project) => <><Text>{formatDateTime(project.createdAt)}</Text><br /><Text type="secondary">{formatDateTime(project.updatedAt)}</Text></> },
    {
      title: "HÀNH ĐỘNG", width: 250, render: (_, project) => {
        const actions = aiVideoProjectActions(project, profile, quota, providers);
        const isPending = Boolean(pending[project.id]);
        return <Space wrap>
          <Link to={aiVideoProjectPath(project)}><Button size="small" icon={<EyeOutlined />}>Chi tiết</Button></Link>
          {actions.canStart && <Button size="small" type="primary" loading={pending[project.id] === "start"} disabled={isPending} onClick={() => runAction(project, "start")}>Bắt đầu</Button>}
          {actions.canStop && <Button size="small" danger loading={pending[project.id] === "stop"} disabled={isPending} onClick={() => runAction(project, "stop")}>Dừng</Button>}
          {actions.canDelete && <Popconfirm title="Xoá dự án AI Video?" description="Toàn bộ job, lịch sử chạy và cấu hình của dự án sẽ bị xoá, không thể hoàn tác." okText="Xoá" cancelText="Huỷ" okButtonProps={{ danger: true }} onConfirm={() => deleteProject(project)}>
            <Button size="small" danger icon={<DeleteOutlined />} loading={pending[project.id] === "delete"} disabled={isPending}>Xoá</Button>
          </Popconfirm>}
        </Space>;
      },
    },
  ];

  const canCreate = status === "ready" && (profile.aiVideoPlanMode === "BYOK" || profile.aiVideoPlanMode === "MANAGED");
  return <>
    <div className="page-heading">
      <div><Title level={3}>{illustrated ? "Video minh họa AI" : "Dự án AI Video"}</Title><Text type="secondary">{illustrated
        ? "AI viết kịch bản, vẽ tranh minh họa từng cảnh, đọc giọng (VOICEVOX/Edge) và dựng video theo lịch."
        : "Nhập chủ đề một lần, hệ thống tự tạo và đăng video theo lịch cho đến khi bạn dừng."}</Text></div>
      <Button type="primary" icon={<PlusOutlined />} disabled={!canCreate} onClick={() => setCreateOpen(true)}>Tạo dự án</Button>
    </div>
    <Card className="criclebox">
      <div className="auto-toolbar ai-video-project-toolbar">
        <Input.Search className="ai-video-project-search" aria-label="Tìm dự án" allowClear enterButton={<SearchOutlined />} placeholder="Tìm dự án" value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={filteredProjects} scroll={{ x: 1250 }} />
    </Card>
    <Modal className="ai-video-project-modal" width={1200} title={illustrated ? "Tạo dự án Video minh họa AI" : "Tạo dự án AI Video"} visible={createOpen} footer={null} destroyOnClose onCancel={() => !creating && setCreateOpen(false)}>
      <AiVideoProjectForm variant={illustrated ? "ILLUSTRATED" : "STANDARD"} profile={profile} providers={providers} workspaceConnections={workspaceConnections} onConnectWorkspace={connectWorkspace} onSubmit={createProject} submitting={creating} />
    </Modal>
  </>;
}
